import type { CSSProperties, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sentry } from './sentry';
import { reloadOnceOnChunkFailure } from './handle-chunk-load-failure';

interface Props {
  module: string;
  children: ReactNode;
}

/**
 * Per-module error boundary. One crashing module shows a named fallback while
 * the sidebar, top-nav, and other modules stay interactive. The top-level
 * boundary in main.tsx is still there as a last resort for anything outside a
 * module route (shell crashes, router failures).
 *
 * Errors flow through Sentry's beforeCapture so event.tags.module is set —
 * operators can filter Sentry by broken module, and the existing PHI walker
 * in initSentryClient's beforeSend still runs.
 *
 * Keyed on location.pathname so navigating via the sidebar (or any router-
 * driven URL change) forces the boundary to remount with fresh state. Without
 * this, Sentry.ErrorBoundary keeps its internal hasError flag across route
 * swaps that don't fully unmount the element, and the fallback stays visible
 * even after the user has navigated to a different page.
 *
 * Fallback is intentionally Tailwind-free: a CSS bundle load failure would
 * take Tailwind with it and we need the recovery UI to render regardless.
 */
export function ModuleErrorBoundary({ module, children }: Props) {
  const location = useLocation();
  return (
    <Sentry.ErrorBoundary
      key={location.pathname}
      beforeCapture={(scope) => {
        scope.setTag('module', module);
      }}
      // Stale-bundle chunk-load failures land here (lazy() rejection caught
      // by Suspense + rethrown into this boundary, never reaching window-level
      // listeners). Detect + reload before the fallback paints.
      onError={reloadOnceOnChunkFailure}
      fallback={() => <ModuleFallback module={module} />}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

function ModuleFallback({ module }: { module: string }) {
  // Both buttons hard-navigate (full page load) rather than client-side
  // reset + navigate. A render error means we can't trust in-memory state —
  // and the broken view is often re-openable via persisted URL params or
  // sessionStorage, which client-side navigate doesn't clear. A hard
  // navigation throws away all React state and pending fetches, which is
  // the only way to guarantee escape from a render loop.
  return (
    <div
      style={{
        padding: 32,
        fontFamily: 'system-ui, sans-serif',
        maxWidth: 560,
        margin: '64px auto',
      }}
    >
      <h1 style={{ marginTop: 0 }}>Something went wrong in {module}.</h1>
      <p>The rest of the portal still works. You can try reloading this page, or go back home.</p>
      <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={buttonStyle(true)}
        >
          Reload
        </button>
        <button
          type="button"
          onClick={() => window.location.assign('/')}
          style={buttonStyle(false)}
        >
          ← Back to home
        </button>
      </div>
    </div>
  );
}

function buttonStyle(primary: boolean): CSSProperties {
  return {
    padding: '8px 16px',
    fontSize: 14,
    fontFamily: 'inherit',
    border: '1px solid #1f2937',
    borderRadius: 6,
    cursor: 'pointer',
    background: primary ? '#1f2937' : 'white',
    color: primary ? 'white' : '#1f2937',
  };
}
